import { ResponsiveContainer, ComposedChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { format, parseISO } from "date-fns";
import { MousePointerClick } from "lucide-react";
import { gbp, gbpK, num, STATUS_META } from "@/lib/format";
import { Panel } from "@/components/PageHeader";
import { Sap } from "@/components/StatusBadge";

const INIT = { width: 600, height: 260 };
const fade = (active, selected) => (selected && !active ? 0.3 : 1);
const monthLabel = (m) => format(parseISO(`${m}-01`), "MMM yy");

const Hint = () => <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400"><MousePointerClick className="h-3 w-3" />Click to drill</span>;

const Tip = ({ active, payload, label }) => active && payload?.length ? (
  <div className="rounded-md border bg-white px-3 py-2 text-xs shadow-lg">
    <div className="mb-1 font-semibold">{payload[0].payload.month ? monthLabel(payload[0].payload.month) : label || payload[0].payload.name || payload[0].payload.region}</div>
    <div>Spend <b>{gbp(payload[0].payload.spend)}</b></div>
    <div>Orders <b>{num(payload[0].payload.orders)}</b></div>
    <div className="mt-1 text-[10px] text-slate-400">Click to drill in</div>
  </div>
) : null;

const MonthChart = ({ data, selected, onPick }) => (
  <Panel title="Spend & orders by month" className="xl:col-span-2" action={<Hint />}>
    <div className="h-64" data-testid="chart-spend-by-month"><ResponsiveContainer initialDimension={INIT}>
      <ComposedChart data={data} margin={{ right: 8 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
        <XAxis dataKey="month" tickFormatter={monthLabel} tickLine={false} axisLine={false} />
        <YAxis yAxisId="l" tickFormatter={gbpK} tickLine={false} axisLine={false} width={60} />
        <YAxis yAxisId="r" orientation="right" tickLine={false} axisLine={false} width={35} />
        <Tooltip content={<Tip />} cursor={{ fill: "rgba(255,0,0,0.05)" }} />
        <Bar yAxisId="l" dataKey="spend" radius={[4, 4, 0, 0]} className="cursor-pointer" onClick={(e) => onPick(e.payload?.month ?? e.month)}>
          {data.map((m) => <Cell key={m.month} fill="#FF0000" fillOpacity={fade(m.month === selected, selected)} data-testid={`month-bar-${m.month}`} />)}
        </Bar>
        <Line yAxisId="r" dataKey="orders" stroke="#0F172A" strokeWidth={2} dot={{ r: 2.5 }} type="monotone" />
      </ComposedChart>
    </ResponsiveContainer></div>
  </Panel>
);

const CategoryBars = ({ data, selected, onPick }) => {
  const total = data.reduce((s, c) => s + c.spend, 0) || 1;
  const max = data[0]?.spend || 1;
  return (
    <Panel title="Spend by category" action={<Hint />}>
      <div className="max-h-64 space-y-2 overflow-y-auto pr-1" data-testid="chart-by-category">
        {data.map((c) => (
          <button key={c.category} onClick={() => onPick(c.category)} data-testid={`category-bar-${c.category}`}
            className="group block w-full text-left transition-opacity" style={{ opacity: fade(c.category === selected, selected) }}>
            <div className="flex justify-between text-xs"><span className="font-medium group-hover:text-[#FF0000]">{c.category}</span>
              <span className="tabular-nums text-slate-500">{gbpK(c.spend)} · {((c.spend / total) * 100).toFixed(1)}%</span></div>
            <div className="mt-1 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-[#00573F] transition-[width] duration-500 group-hover:bg-[#FF0000]" style={{ width: `${(c.spend / max) * 100}%` }} /></div>
          </button>))}
      </div>
    </Panel>
  );
};

const RegionChart = ({ data, selected, onPick }) => (
  <Panel title="Spend by region" action={<Hint />}>
    <div className="h-64" data-testid="chart-by-region"><ResponsiveContainer initialDimension={INIT}>
      <BarChart data={data}><CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
        <XAxis dataKey="region" tickLine={false} axisLine={false} interval={0} tick={{ fontSize: 10 }} />
        <YAxis tickFormatter={gbpK} tickLine={false} axisLine={false} width={55} />
        <Tooltip content={<Tip />} cursor={{ fill: "rgba(0,87,63,0.05)" }} />
        <Bar dataKey="spend" radius={[4, 4, 0, 0]} className="cursor-pointer" onClick={(e) => onPick(e.payload?.region ?? e.region)}>
          {data.map((r) => <Cell key={r.region} fill="#00573F" fillOpacity={fade(r.region === selected, selected)} />)}
        </Bar></BarChart>
    </ResponsiveContainer></div>
  </Panel>
);

const LocationChart = ({ data, selected, onPick }) => (
  <Panel title="Top stores & concessions" className="xl:col-span-2" action={<Hint />}>
    <div className="h-64" data-testid="chart-top-locations"><ResponsiveContainer initialDimension={INIT}>
      <BarChart data={data} layout="vertical" margin={{ left: 10 }}>
        <XAxis type="number" tickFormatter={gbpK} tickLine={false} axisLine={false} />
        <YAxis type="category" dataKey="name" width={200} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
        <Tooltip content={<Tip />} cursor={{ fill: "rgba(197,160,89,0.08)" }} />
        <Bar dataKey="spend" radius={[0, 4, 4, 0]} className="cursor-pointer" onClick={(e) => onPick(e.payload ?? e)}>
          {data.map((l) => <Cell key={l.code} fill="#C5A059" fillOpacity={fade(l.code === selected, selected)} />)}
        </Bar></BarChart>
    </ResponsiveContainer></div>
  </Panel>
);

const COLORS = { pending_approval: "#F59E0B", approved: "#00573F", dispatched: "#0284C7", delivered: "#94A3B8", rejected: "#FF0000" };
const StatusStrip = ({ data, selected, onPick }) => {
  const total = data.reduce((s, x) => s + x.orders, 0) || 1;
  return (
    <Panel title="Orders by status" action={<Hint />}>
      <div className="flex h-4 overflow-hidden rounded-full" data-testid="chart-by-status">
        {data.filter((s) => s.orders).map((s) => (
          <button key={s.status} onClick={() => onPick(s.status)} title={STATUS_META[s.status].label}
            className="h-full transition-opacity hover:opacity-80" style={{ width: `${(s.orders / total) * 100}%`, background: COLORS[s.status], opacity: fade(s.status === selected, selected) }} />))}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
        {data.map((s) => (
          <button key={s.status} onClick={() => onPick(s.status)} data-testid={`status-drill-${s.status}`}
            className={`rounded-md border p-2 text-left transition-colors hover:border-slate-900 ${s.status === selected ? "border-slate-900 bg-slate-50" : ""}`}>
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500"><span className="h-2 w-2 rounded-full" style={{ background: COLORS[s.status] }} />{STATUS_META[s.status].label}</div>
            <div className="font-display text-lg font-bold tabular-nums">{num(s.orders)}</div>
          </button>))}
      </div>
    </Panel>
  );
};

const TopProducts = ({ data, selected, onPick }) => (
  <Panel title="Top products by spend" action={<Hint />}>
    <table className="w-full text-sm" data-testid="top-products-table"><tbody>
      {data.map((p) => (
        <tr key={p.sap_code} onClick={() => onPick(p.sap_code)} data-testid={`top-product-${p.sap_code}`}
          className={`cursor-pointer border-t transition-colors first:border-0 hover:bg-red-50/60 ${p.sap_code === selected ? "bg-red-50" : ""}`}>
          <td className="py-2 pr-2"><Sap code={p.sap_code} /></td><td className="py-2 pr-2">{p.title}</td>
          <td className="py-2 text-right tabular-nums text-slate-500">{num(p.qty)}</td>
          <td className="py-2 pl-3 text-right font-semibold tabular-nums">{gbp(p.spend)}</td></tr>))}
    </tbody></table>
  </Panel>
);

export const DashboardCharts = ({ d, filters, drill }) => {
  const selMonth = filters.preset === "custom" && filters.date_from?.endsWith("-01") ? filters.date_from.slice(0, 7) : null;
  const pickMonth = (m) => {
    const end = new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)), 0);
    drill({ preset: "custom", date_from: `${m}-01`, date_to: format(end, "yyyy-MM-dd") });
  };
  const pickLoc = (l) => drill(l.kind === "concession" ? { store: l.store_code, concession: l.code } : { store: l.code, concession: "none" });
  const selLoc = filters.concession !== "all" && filters.concession !== "none" ? filters.concession : filters.store !== "all" ? filters.store : null;
  const val = (v) => (v === "all" ? null : v);
  return (
    <div className="grid gap-4 xl:grid-cols-3">
      <MonthChart data={d.spend_by_month} selected={selMonth} onPick={pickMonth} />
      <CategoryBars data={d.by_category} selected={val(filters.category)} onPick={(c) => drill({ category: c })} />
      <RegionChart data={d.by_region} selected={val(filters.region)} onPick={(r) => drill({ region: r, store: "all", concession: "all" })} />
      <LocationChart data={d.top_locations} selected={selLoc} onPick={pickLoc} />
      <StatusStrip data={d.by_status} selected={val(filters.status)} onPick={(s) => drill({ status: s })} />
      <div className="xl:col-span-2"><TopProducts data={d.top_products} selected={val(filters.sap_code)} onPick={(s) => drill({ sap_code: s })} /></div>
    </div>
  );
};
